import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { ProductCover } from '@/components/digital/ProductCard';
import { BuyerForm } from '@/components/digital/BuyerForm';
import { RefundNotice } from '@/components/digital/RefundNotice';
import { digitalApi, formatPrice } from '@/lib/digitalApi';
import { usePageMeta } from '@/lib/usePageMeta';
import { ArticleView } from '@/components/digital/ArticleView';
import { RESOURCE_SECTIONS } from '@/constants/resourceSections';

const ClaimedPanel = ({ claim }) => (
  <div className="bg-white rounded-2xl p-8 border border-gold/40" data-testid="digital-claim-success">
    <div className="eyebrow !text-gold mb-4">Your download is ready</div>
    <p className="text-matte/70 leading-relaxed">
      We also sent a copy of your secure link to {claim.email_masked}.
    </p>
    <Link to={claim.download_path} className="btn-primary mt-8" data-testid="digital-claim-download-link">
      <Download size={14} strokeWidth={1.6} /> Go to Download
    </Link>
  </div>
);

export default function DigitalProductDetail() {
  const { slug, section = 'digital-products' } = useParams();
  const sectionLabel = RESOURCE_SECTIONS[section]?.label || 'Resources';
  const backTo = section === 'journal' ? '/journal' : `/resources/${section}`;
  const [params] = useSearchParams();
  const [product, setProduct] = useState(undefined);
  const [claim, setClaim] = useState(null);

  useEffect(() => { setProduct(undefined); digitalApi.get(slug, section).then(setProduct).catch(() => setProduct(null)); }, [slug, section]);
  usePageMeta({
    title: product?.title || 'Digital Product',
    description: product?.short_description || 'A digital resource from Toia Wallace.',
    path: `/resources/${section}/${slug}`,
    ...(product?.unlisted ? { robots: 'noindex, nofollow' } : {}),
  });

  if (product === undefined) return <div className="bg-ivory min-h-screen pt-56 px-6 text-center text-matte/50">Loading…</div>;
  if (product === null) {
    return (
      <div className="bg-ivory min-h-screen pt-56 pb-24 px-6 text-center" data-testid="digital-product-not-found">
        <h1 className="font-serif-display text-5xl tracking-tighter text-matte">This product isn&rsquo;t available.</h1>
        <Link to={backTo} className="btn-primary mt-10">Browse {sectionLabel}</Link>
      </div>
    );
  }

  if (product.content_type === 'article') {
    return <ArticleView article={product} backTo={backTo} backLabel={sectionLabel} />;
  }

  return (
    <div className="bg-ivory" data-testid="digital-product-page">
      <section className="pt-36 lg:pt-48 pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <Link to={backTo} className="btn-ghost mb-12" data-testid="digital-back-to-library">
            <ArrowLeft size={14} strokeWidth={1.6} /> {section === 'digital-products' ? 'The Library' : sectionLabel}
          </Link>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <ProductCover product={product} className="aspect-[4/5] luxury-shadow" />
            </div>
            <div className="lg:col-span-7 min-w-0">
              <div className="flex flex-wrap items-center gap-3 text-[10px] font-label tracking-widest uppercase text-taupe">
                <span>{product.format || 'Digital'}</span>
                {product.category_name && (<><span className="w-1 h-1 rounded-full bg-gold" aria-hidden /><span>{product.category_name}</span></>)}
                {product.file_type && (<><span className="w-1 h-1 rounded-full bg-gold" aria-hidden /><span>{product.file_type}</span></>)}
              </div>
              <h1 className="mt-6 font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tightest text-matte break-words" data-testid="digital-product-title">
                {product.title}
              </h1>
              {product.short_description && (
                <p className="mt-6 font-serif-display italic text-2xl text-taupe leading-snug max-w-2xl">{product.short_description}</p>
              )}
              <div className="mt-8 font-serif-display text-4xl text-matte" data-testid="digital-product-price">
                {formatPrice(product.price_cents, product.is_free)}
              </div>
              {params.get('checkout') === 'cancelled' && (
                <p className="mt-6 text-sm text-matte/70 bg-sand/50 rounded-xl px-5 py-4" data-testid="digital-checkout-cancelled">
                  Checkout was cancelled. You have not been charged.
                </p>
              )}
              <div className="mt-10 max-w-lg">
                {claim ? <ClaimedPanel claim={claim} /> : <BuyerForm product={product} onClaimed={setClaim} />}
              </div>
              {product.description && (
                <div className="mt-16 pt-10 border-t border-taupe/30 space-y-5 text-matte/75 leading-relaxed max-w-2xl" data-testid="digital-product-description">
                  {product.description.split(/\n{2,}/).map((para, i) => <p key={i} className="whitespace-pre-line">{para}</p>)}
                </div>
              )}
              {!product.is_free && <RefundNotice className="mt-12 max-w-2xl" />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
